import type { UnlockId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { TFunction } from '@/core/i18n/translator';
import type { MissingReason } from '@/core/domain/conditions';
import { unlockStatus } from '@/core/domain/unlocks';
import { Page } from '@/ui/components/Page';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { useSaveDerived, useServices } from '@/ui/services/ServicesContext';
import styles from './UnlocksScreen.module.css';
import { Icon } from '@/ui/components/Icon';

/** One "còn thiếu" line per reason (content-schemas §2.2), in words a player understands. */
export function missingText(m: MissingReason, t: TFunction, content: ContentRegistry): string {
  switch (m.kind) {
    case 'stars':
      return t('ui.unlocks.need.stars', { missing: m.need - m.have, need: m.need });
    case 'task': {
      const def = content.suggestions.find((d) => d.id === m.taskId);
      return def?.kind === 'tutorial'
        ? t('ui.unlocks.need.tutorial')
        : t('ui.unlocks.need.task', { name: def ? t(def.titleKey) : '' });
    }
    case 'unlock': {
      const def = content.unlocks.get(m.unlockId);
      return t('ui.unlocks.need.unlock', { name: def ? t(def.nameKey) : '' });
    }
    case 'friendship': {
      const npc = content.npcs.get(m.npcId);
      return t('ui.unlocks.need.friendship', { name: npc ? t(npc.nameKey) : '', level: m.needLevel });
    }
    case 'coziness':
      return t('ui.unlocks.need.coziness', { room: m.roomId, level: m.needLevel });
    case 'achievement': {
      const a = content.achievements.find((x) => x.id === m.achievementId);
      return t('ui.unlocks.need.achievement', { name: a ? t(a.nameKey) : '' });
    }
    case 'coins':
      return t('ui.unlocks.need.coins', { amount: m.amount });
    case 'chapter': {
      const ch = content.chapters.get(m.chapterId);
      return t('ui.unlocks.need.chapter', { name: ch ? t(ch.titleKey) : '' });
    }
    case 'miniGame': {
      const mg = content.miniGames.get(m.miniGameId);
      return t('ui.unlocks.need.mini_game', { name: mg ? t(mg.nameKey) : '', level: m.level });
    }
    case 'studio':
      return t('ui.unlocks.need.studio', { level: m.needLevel });
    default:
      return t('ui.unlocks.need.other');
  }
}

type Row =
  | { id: UnlockId; kind: 'granted' }
  | { id: UnlockId; kind: 'purchasable'; cost: number; affordable: boolean }
  | { id: UnlockId; kind: 'locked'; missing: MissingReason[] };

/**
 * Every unlock and what it still needs (M3-03 "màn danh sách unlock"), opened from "Sắp mở". Open ones are
 * ticked and struck through, paid areas show their price, surprises stay a surprise until they open.
 */
export function UnlocksScreen({
  onBack,
  onExpand,
}: {
  onBack: () => void;
  onExpand: (unlockId: UnlockId) => void;
}) {
  const { t } = useI18n();
  const { content } = useServices();
  const rows = useSaveDerived((s) =>
    [...content.unlocks.values()]
      .sort((a, b) => a.order - b.order)
      .map((d): Row => {
        const st = unlockStatus(content, s, d.id);
        return st.kind === 'granted'
          ? { id: d.id, kind: 'granted' }
          : st.kind === 'purchasable'
            ? { id: d.id, kind: 'purchasable', cost: st.cost, affordable: st.affordable }
            : { id: d.id, kind: 'locked', missing: st.missing };
      }),
  );
  const open = rows.filter((r) => r.kind === 'granted').length;

  return (
    <Page
      title={t('ui.unlocks.title')}
      onBack={onBack}
      testId="unlocks"
      aside={<span className={styles.count}>{t('ui.unlocks.count', { open, total: rows.length })}</span>}
    >
      <ul className={styles.list}>
        {rows.map((r) => {
          const def = content.unlocks.get(r.id);
          if (!def) return null;
          const secret = r.kind === 'locked' && !def.previewWhenLocked;
          return (
            <li key={r.id} className={styles.row} data-state={r.kind} data-testid={`unlock-${r.id}`}>
              <span className={styles.name}>
                {r.kind === 'granted' && <Icon name="check" size={20} className={styles.tick} />}
                {secret ? t('ui.unlocks.secret') : t(def.nameKey)}
                {r.kind === 'granted' && <span className="visually-hidden">{t('ui.unlocks.done')}</span>}
              </span>
              {r.kind === 'purchasable' ? (
                <button
                  type="button"
                  className={styles.expand}
                  onClick={() => {
                    onExpand(r.id);
                  }}
                >
                  {t('ui.unlocks.expand', { price: r.cost })}
                </button>
              ) : r.kind === 'locked' ? (
                <span className={styles.need}>
                  {secret
                    ? t('ui.unlocks.secret_hint')
                    : r.missing.length === 0
                      ? t('ui.unlocks.need.other')
                      : r.missing.map((m) => missingText(m, t, content)).join(', ')}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Page>
  );
}
